import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Tabs, Tab, Divider, Alert, Button, Chip, Stack, Breadcrumbs, Link, Typography } from '@mui/material';
import Carousel from "react-bootstrap/Carousel"; // Bootstrap Carousel
import "bootstrap/dist/css/bootstrap.min.css"; // Bootstrap styles
import BASE_API_URL from '../apis/index';
import { UserContext } from '../App';
import CompProductsSectionIngManf from './UserCompanyDetails/CompProductsSectionIngManf';
import CompProductsSectionIngDist from './UserCompanyDetails/CompProductsSectionIngDist';
import CompServicesSection from './UserCompanyDetails/CompServicesSection';
import CompServicesSectionCRO from './UserCompanyDetails/CompServicesSectionCRO';

export default function UserCompanyDetails() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();

    const [company, setCompany] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [showProductsTab, setShowProductsTab] = useState(false);
    const [showServicesTab, setShowServicesTab] = useState(false);
    const [showCROServicesTab, setShowCROServicesTab] = useState(false);
    const [companyTypes, setCompanyTypes] = useState([]);
    const [companyStatus, setCompanyStatus] = useState("pending");
    // const [productsLen, setProductsLen] = useState(0);

    const useQuery = () => { return new URLSearchParams(useLocation().search); };
    const compId = useQuery().get("compId");

    useEffect(() => {
        async function fetchCompanyDetails() {
            try {
                const authToken = localStorage.getItem('authToken');
                const res = await axios.get(`${BASE_API_URL}/api/seller/company/${compId}`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                setCompany(res.data);

                const parsedCompanyTypes = JSON.parse(res.data.company_types || "[]");
                setCompanyTypes(parsedCompanyTypes);
                setCompanyStatus(res.data.status);

                const statusApproved = res.data.status?.toLowerCase() === "approved";
                const includesSupplier = parsedCompanyTypes.includes("Ingredient Supplier");
                const includesManufacturer = parsedCompanyTypes.includes("Contract Manufacturer");
                const includesCRO = parsedCompanyTypes.includes("CRO");

                // Set tab visibility
                setShowProductsTab(statusApproved && includesSupplier);
                setShowServicesTab(statusApproved && includesManufacturer);
                setShowCROServicesTab(statusApproved && includesCRO);
            } catch (err) {
                console.error('Failed to fetch company data', err);
            }
        }
        fetchCompanyDetails();
    }, [compId]);

    const handleTabChange = (event, newValue) => { setTabIndex(newValue); };
    const handleEditAndResubmit = () => { navigate(`/user-company-details-edit?compId=${company.id}&compName=${company.company_name}`); };

    // Deletes Company
    // const handleDeleteCompany = async () => {
    //     const confirmDelete = window.confirm(`Are you sure you want to delete "${company.company_name}"?`);
    //     if (!confirmDelete) return;
    //     if ((productsLen ?? 0) > 0) {
    //         alert('This company has products. Please remove them before deleting this company.');
    //         return;
    //     }
    //     try {
    //         const authToken = localStorage.getItem('authToken');
    //         const res = await axios.delete(`${BASE_API_URL}/api/user/user-companies/delete-comp`, {
    //             params: { compId: company.id },
    //             headers: { Authorization: `Bearer ${authToken}` }
    //         });
    //         if (res.data.status === "ok") {
    //             alert("✅ Company deleted successfully!");
    //             navigate('/user-companies');
    //         } else {
    //             alert("⚠️ Unable to delete company. Please try again.");
    //         }
    //     } catch (err) {
    //         console.error("Company deleting error: ", err);
    //         alert("❌ Something went wrong while deleting the company. Try again later!");
    //     }
    // };

    const renderDocRow = (label, doc) => (
        <p>
            <strong>{label}:</strong>{" "}
            {doc ? (
                <>
                    Uploaded{" "}
                    <Button variant="outlined" size="small" sx={{ ml: 2 }} onClick={() => window.open(`${BASE_API_URL}/uploads/user_comp_docs/${doc}`, "_blank")}> View </Button>
                </>
            ) : (
                "Not uploaded"
            )}
        </p>
    );

    const maskAccountNumber = (accNo) => {
        if (!accNo) return "";
        const visibleDigits = accNo.slice(-4);
        return `XXXX XXXX ${visibleDigits}`;
    };

    const maskIFSC = (ifsc) => {
        if (!ifsc) return "";
        const visible = ifsc.slice(0, 4);
        const masked = "X".repeat(ifsc.length - 4);
        return `${visible}${masked}`;
    };

    if (!company) return <p>Loading company details...</p>;

    const isRejected = companyStatus === 'Rejected';
    const bgColor = companyStatus === 'Approved' ? '#E8F5E9' : companyStatus === 'Pending' ? '#FFF8E1' : '#FFEBEE';
    const textColor = companyStatus === 'Approved' ? '#388E3C' : companyStatus === 'Rejected' ? '#D32F2F' : '#F57C00';

    return (
        <Wrapper className='container'>
            {/* BreadCrumbs */}
            <Breadcrumbs aria-label="breadcrumb" className='mb-4 mt-2 pageBreadCrumbs bg-light py-2 px-4 rounded'>
                <Link underline="hover" color="inherit" href="/user-companies"> Companies </Link>
                <Typography sx={{ color: 'text.primary' }}> {company.company_name} </Typography>
            </Breadcrumbs>

            {/* 1st Section */}
            <div className="p-3 mb-4">
                <div className="d-flex align-items-center justify-content-between gap-4">
                    <div className="LeftSide">
                        <div className="CompLogo">
                            <img
                                src={`${BASE_API_URL}/uploads/user_comp_docs/${company.company_logo}`}
                                alt="Company Logo"
                            />
                        </div>
                        <Typography variant="h5" className="txt_color_primary">
                            {company.company_name}
                        </Typography>
                        <div className='my-2'>
                            {companyTypes.map((type) => (
                                <Chip
                                    key={type}
                                    label={type}
                                    color="warning"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                    className="rounded"
                                />
                            ))}
                        </div>

                        <Typography variant="body2" mt={2} className='mb-2'> GST Number: {company.gst_number} </Typography>
                        {/* <Alert icon={<CheckIcon fontSize="inherit" />} severity="warning" className="mt-3" style={{ width: '320px' }} > Status: <strong>{company.status}</strong> </Alert> */}
                        <div
                            className='px-4 py-2 mb-0 status-box'
                            style={{
                                backgroundColor: bgColor,
                                color: textColor,
                                width: "auto"
                            }}>
                            <span>Status:</span> {companyStatus}
                        </div>
                    </div>

                    <div className="RightSide">
                        {company.company_images && (
                            <Carousel>
                                {JSON.parse(company.company_images).map((img, idx) => (
                                    <Carousel.Item key={idx}>
                                        <img
                                            className="d-block w-100"
                                            src={`${BASE_API_URL}/uploads/user_comp_docs/${img}`}
                                            alt={`company-img-${idx}`}
                                            style={{
                                                width: "100%",
                                                maxHeight: "300px",
                                                objectFit: "cover",
                                                borderRadius: "10px",
                                            }}
                                        />
                                    </Carousel.Item>
                                ))}
                            </Carousel>
                        )}
                    </div>
                </div>
            </div>

            <div className='TabSection border rounded-4 p-0'>
                <div className="d-flex justify-content-between align-items-center border-bottom">
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant='scrollable'
                        indicatorColor="primary"
                        textColor="primary"
                        selectionFollowsFocus
                        className='pb-0'
                    >
                        <Tab label='Company Details' />
                        <Tab label='Statutory Details' />
                        <Tab label='Bank Details' className='me-auto' />
                        {isRejected && <Tab label='Status' />}
                    </Tabs>
                    {companyStatus === "Approved" && (
                        <Button
                            variant="outlined"
                            color='primary'
                            size="medium"
                            className="mb-0 shadow-none bg-light mx-2 rounded-3"
                            onClick={handleEditAndResubmit}
                        >
                            Edit Company Details
                        </Button>
                    )}
                </div>

                <Divider sx={{ mb: 2 }} />

                {tabIndex === 0 && (
                    <div className='px-4'>
                        <div className='mb-3'><strong className='text-dark fw-medium'>Brief:</strong> {company.brief}</div>
                        <div className='mb-3'><strong className='text-dark fw-medium'>Address:</strong> {company.address}</div>
                        <div className='mb-3'><strong className='text-dark fw-medium'>Country Of Origin:</strong> {company.country_of_origin}</div>
                        <Stack direction={"row"} divider={<Divider orientation="vertical" flexItem />} spacing={2}>
                            <div>
                                <strong className='text-dark fw-medium'>Email:</strong>
                                {JSON.parse(company.emails).map((itm, idx) => {
                                    return <span key={idx} className="rounded px-3 py-1 bg-light border ms-2"> {itm} </span>
                                })}
                            </div>
                            <div>
                                <strong className='text-dark fw-medium'>Mobile Numbers:</strong>
                                {JSON.parse(company.mobile_numbers).map((num, idx) => {
                                    return <span key={idx} className="rounded px-3 py-1 bg-light border ms-2"> {num} </span>
                                })}
                            </div>
                        </Stack>
                    </div>
                )}

                {tabIndex === 1 && (
                    <div className='px-4 row'>
                        <div className="px-4 row">
                            <div className="col-md-6">
                                <p><strong>PAN Number:</strong> {company.pan_number}</p>
                                <p><strong>GST Number:</strong> {company.gst_number}</p>
                                <p><strong>CIN Code:</strong> {company.cin_code}</p>
                                <p><strong>Annual Turnover:</strong> {company.annual_turnover}</p>
                                <p><strong>Vendor Status:</strong> {company.vendor_status}</p>
                                <p><strong>Vendor Type:</strong> {company.vendor_type}</p>
                            </div>
                            <div className="col-md-6">
                                {renderDocRow("PAN Document", company.pan_doc)}
                                {renderDocRow("GST Document", company.gst_doc)}
                                {renderDocRow("CIN Document", company.cin_doc)}
                                {renderDocRow("RC Document", company.rc_doc)}
                                {renderDocRow("LLP Certificate", company.llp_cert)}
                            </div>
                        </div>
                    </div>
                )}

                {tabIndex === 2 && (
                    <div className='px-4'>
                        <p><strong>Account Number:</strong> {maskAccountNumber(company.account_number)}</p>
                        <p><strong>IFSC:</strong> {maskIFSC(company.ifsc)}</p>
                        <p><strong>Branch:</strong> {company.branch}</p>
                        <p><strong>Bank:</strong> {company.bank}</p>
                        <p><strong>Account Holder Name:</strong> {company.account_holder_name}</p>
                    </div>
                )}

                {isRejected && tabIndex === (showProductsTab ? 4 : 3) && (
                    <div>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <strong>Rejected:</strong> {company.rejection_reason}
                        </Alert>
                        <Button variant="contained" color="warning" className='shadow-none' onClick={handleEditAndResubmit}>
                            Edit & Resubmit
                        </Button>
                    </div>
                )}
            </div>

            {company.status === "Approved" && companyTypes.includes("Ingredient Manufacturer") && (
                <CompProductsSectionIngManf compId={company.id} compName={company.company_name} compTypes={company.company_types} userId={user.userId} />
            )}

            {company.status === "Approved" && companyTypes.includes("Ingredient Distributor") && (
                <CompProductsSectionIngDist compId={company.id} compName={company.company_name} compTypes={company.company_types} userId={user.userId} />
            )}
            
            {company.status === "Approved" && showServicesTab && (
                <CompServicesSection compId={company.id} compName={company.company_name} compTypes={company.company_types} userId={user.userId} />
            )}
            
            {company.status === "Approved" && showCROServicesTab && (
                <CompServicesSectionCRO compId={company.id} compName={company.company_name} compTypes={company.company_types} userId={user.userId} />
            )}
        </Wrapper>
    );
}

const Wrapper = styled.div`
.txt_color_secondary {
  color: #333;
}
.status-box {
  font-weight: bold;
  border-radius: 8px;
  padding: 10px 20px;
}
.pageBreadCrumbs{
  a:hover {
      text-decoration: none;
      color: var(--secondary-color);
  }
  
}
.editCompBtn{
    color: #1976d2;
    border: 1px solid #1976d2;
    &:hover{
        background: #e9f4ffff;
    }
}
.deleteCompBtn{
    svg path{ fill: red !important;}
    &:hover{
        svg path{ fill: white !important; }
    }
}
.CompLogo{
    img {
      width: 100px;
      height: 100px;
      object-fit: contain;
    }
}
.TabSection{ min-height: 320px; }
.LeftSide{ flex: 1; }
.RightSide{ flex: 1; }
`;
